import { keyframes } from '@emotion/react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadIcon from '@mui/icons-material/Upload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { Loading } from '@/core/components';
import { getRaces, importRaceRunners, type RaceRunnerInput } from '@/core/services/operator';
import type { Race } from '@/core/types';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** 運営管理画面用グラデーション（黄→赤） */
const operatorGradient = 'linear-gradient(to right, #ffff00, #ff0000)';

export function ImportPage() {
  const [races, setRaces] = useState<Race[]>([]);
  const [selectedRaceId, setSelectedRaceId] = useState('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<RaceRunnerInput[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);

  const fetchRaces = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const racesData = await getRaces();
      setRaces(racesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : '大会の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRaces();
  }, [fetchRaces]);

  const selectedRace = races.find((p) => p.id === selectedRaceId);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedRace) return;

    setFileName(file.name);
    setError(null);
    setParsedData([]);

    try {
      // SJIS として読み込み
      const buffer = await file.arrayBuffer();
      const decoder = new TextDecoder('shift-jis');
      const text = decoder.decode(buffer);

      // CSV をパース（ヘッダなし）
      const lines = text.split('\n').filter((line) => line.trim());
      const data: RaceRunnerInput[] = [];

      for (const line of lines) {
        const cols = line.split(',').map((col) => col.trim());
        if (cols.length < 5) continue;

        const [bib, fullName, fullNameKana, birth, email] = cols;

        data.push({
          bib,
          fullName,
          fullNameKana,
          birth,
          email,
          raceId: selectedRaceId,
          raceAlias: selectedRace.alias,
        });
      }

      setParsedData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CSVの読み込みに失敗しました');
    }
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      setError('インポートするデータがありません');
      return;
    }

    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const count = await importRaceRunners(parsedData);
      setSuccess(`${count}件のランナー情報をインポートしました`);
      setParsedData([]);
      setFileName(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'インポートに失敗しました');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3, animation: `${fadeInUp} 0.6s ease-out` }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, animation: `${fadeInUp} 0.6s ease-out` }}>
          {success}
        </Alert>
      )}

      {/* 設定カード */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* 大会選択 */}
          <FormControl fullWidth required>
            <InputLabel>大会を選択</InputLabel>
            <Select
              value={selectedRaceId}
              label="大会を選択"
              onChange={(e) => {
                setSelectedRaceId(e.target.value);
                setParsedData([]);
                setFileName(null);
                setSuccess(null);
              }}
            >
              {races.map((race) => (
                <MenuItem key={race.id} value={race.id}>
                  {race.raceName} ({race.alias})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ファイルアップロード */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={!selectedRaceId}
              sx={{
                borderRadius: '16px',
                border: '2px solid transparent',
                backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                color: '#1D1B20',
                '&:hover': {
                  backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${operatorGradient}`,
                },
                '&:disabled': {
                  opacity: 0.5,
                  backgroundImage: 'none',
                  border: '2px solid',
                  borderColor: 'divider',
                },
              }}
            >
              CSVファイルを選択
              <input type="file" hidden accept=".csv" onChange={handleFileChange} />
            </Button>
            {fileName && (
              <Typography variant="body2" color="text.secondary">
                {fileName}
              </Typography>
            )}
          </Box>

          {/* CSVフォーマット説明 */}
          <Alert severity="info" sx={{ borderRadius: '8px' }}>
            <Typography variant="body2">
              <strong>CSVフォーマット</strong>（ヘッダなし、SJIS）
              <br />
              <code style={{ backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 6px', borderRadius: '4px' }}>
                ゼッケン, 氏名, 氏名ｶﾅ（空文字可）, 生年月日（空文字可）, メール（空文字可）
              </code>
            </Typography>
          </Alert>
        </Box>
      </Paper>

      {/* プレビュー */}
      {parsedData.length > 0 && (
        <Paper
          sx={{
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            animation: `${fadeInUp} 0.6s ease-out 0.2s both`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'action.hover',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                プレビュー
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {parsedData.length}件
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importing}
              startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
              sx={{
                background: operatorGradient,
                color: '#1D1B20',
                borderRadius: '16px',
                '&:hover': {
                  background: operatorGradient,
                  filter: 'brightness(0.95)',
                },
                '&:disabled': {
                  background: operatorGradient,
                  opacity: 0.6,
                },
              }}
            >
              {importing ? 'インポート中...' : 'インポート実行'}
            </Button>
          </Box>

          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>ゼッケン</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>氏名</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>氏名（カナ）</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>生年月日</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>メール</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedData.slice(0, 100).map((row) => (
                  <TableRow key={`${row.bib}-${row.raceId || row.raceAlias}`} hover>
                    <TableCell>{row.bib}</TableCell>
                    <TableCell>{row.fullName}</TableCell>
                    <TableCell>{row.fullNameKana}</TableCell>
                    <TableCell>{row.birth}</TableCell>
                    <TableCell>{row.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {parsedData.length > 100 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}
            >
              ※ 最初の100件のみ表示
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
